import { type BadgeItem } from '@/components/molecules/badge/BadgeRow';
import {
  type GetHandoffsQuery,
  type HandoffSeverity,
  type HandoffStatus,
  type HandoffType,
} from '@/generated/usermobile-bff/graphql';

export type HandoffItem = GetHandoffsQuery['handoffs']['data'][number];

export type HandoffAction = 'claim' | 'release';

export type HandoffPrimaryAction = 'call' | 'sms' | 'none';

export const HANDOFF_TYPE_TRANSLATION_KEYS: Record<HandoffType, string> = {
  MANUAL_PHONE_CALL: 'aiTasks.handoffType.manualPhoneCall',
  PRINT_MATERIALS_SENT_MAIL: 'aiTasks.handoffType.printMaterialsSentMail',
  MEETING_APPOINTMENT_SECURED: 'aiTasks.handoffType.meetingAppointmentSecured',
  SCHEDULE_MEETING_APPOINTMENT: 'aiTasks.handoffType.scheduleMeetingAppointment',
  SEND_DOCUMENTS: 'aiTasks.handoffType.sendDocuments',
  PROPERTY_AVAILABILITY_CHECK: 'aiTasks.handoffType.propertyAvailabilityCheck',
};

export const HANDOFF_TYPE_DETAIL_TITLE_KEYS: Record<HandoffType, string> = {
  MANUAL_PHONE_CALL: 'aiTasks.detail.title.manualPhoneCall',
  PRINT_MATERIALS_SENT_MAIL: 'aiTasks.detail.title.printMaterialsSentMail',
  MEETING_APPOINTMENT_SECURED: 'aiTasks.detail.title.meetingAppointmentSecured',
  SCHEDULE_MEETING_APPOINTMENT: 'aiTasks.detail.title.scheduleMeetingAppointment',
  SEND_DOCUMENTS: 'aiTasks.detail.title.sendDocuments',
  PROPERTY_AVAILABILITY_CHECK: 'aiTasks.detail.title.propertyAvailabilityCheck',
};

const HANDOFF_CARD_DESCRIPTION_KEYS: Record<HandoffType, string> = {
  MANUAL_PHONE_CALL: 'aiTasks.card.defaultDescription.manualPhoneCall',
  MEETING_APPOINTMENT_SECURED: 'aiTasks.card.defaultDescription.meetingAppointmentSecured',
  SCHEDULE_MEETING_APPOINTMENT: 'aiTasks.card.defaultDescription.scheduleMeetingAppointment',
  SEND_DOCUMENTS: 'aiTasks.card.defaultDescription.sendDocuments',
  PRINT_MATERIALS_SENT_MAIL: 'aiTasks.card.defaultDescription.printMaterialsSentMail',
  PROPERTY_AVAILABILITY_CHECK: 'aiTasks.card.defaultDescription.propertyAvailabilityCheck',
};

const HANDOFF_DEFAULT_DESCRIPTION_KEYS: Record<HandoffType, string | null> = {
  MANUAL_PHONE_CALL: 'aiTasks.detail.defaultDescription.manualPhoneCall',
  MEETING_APPOINTMENT_SECURED: 'aiTasks.detail.defaultDescription.meetingAppointmentSecured',
  SCHEDULE_MEETING_APPOINTMENT: 'aiTasks.detail.defaultDescription.scheduleMeetingAppointment',
  SEND_DOCUMENTS: 'aiTasks.detail.defaultDescription.sendDocuments',
  PRINT_MATERIALS_SENT_MAIL: 'aiTasks.detail.defaultDescription.printMaterialsSentMail',
  PROPERTY_AVAILABILITY_CHECK: 'aiTasks.detail.defaultDescription.propertyAvailabilityCheck',
};

interface HandoffDescriptionInput {
  readonly handoffType: HandoffType;
  readonly notes: string | null | undefined;
  readonly contactName: string | null | undefined;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
}

export const getHandoffCardDescription = ({
  handoffType,
  notes,
  contactName,
  t,
}: HandoffDescriptionInput): string | null => {
  if (notes != null && notes.trim() !== '') {
    return notes;
  }

  if (contactName == null || contactName === '') {
    return null;
  }

  return t(HANDOFF_CARD_DESCRIPTION_KEYS[handoffType], { name: contactName });
};

export const getHandoffDescription = ({
  handoffType,
  notes,
  contactName,
  t,
}: HandoffDescriptionInput): string | null => {
  if (notes != null && notes.trim() !== '') {
    return notes;
  }

  const defaultKey = HANDOFF_DEFAULT_DESCRIPTION_KEYS[handoffType];

  if (defaultKey == null || contactName == null || contactName === '') {
    return null;
  }

  return t(defaultKey, { name: contactName });
};

// Only the types that expect the user to reach the customer carry a contact
// action; the rest are reports the user just reads and hands the baton back.
const HANDOFF_PRIMARY_ACTIONS: Record<HandoffType, HandoffPrimaryAction> = {
  MANUAL_PHONE_CALL: 'call',
  SCHEDULE_MEETING_APPOINTMENT: 'sms',
  PROPERTY_AVAILABILITY_CHECK: 'sms',
  MEETING_APPOINTMENT_SECURED: 'none',
  SEND_DOCUMENTS: 'none',
  PRINT_MATERIALS_SENT_MAIL: 'none',
};

export const getHandoffPrimaryAction = (handoffType: HandoffType): HandoffPrimaryAction =>
  HANDOFF_PRIMARY_ACTIONS[handoffType];

interface ContactMobileNumbers {
  readonly personalMobileNumber: string | null;
  readonly workMobileNumber: string | null;
}

export const getContactPhoneNumber = ({
  personalMobileNumber,
  workMobileNumber,
}: ContactMobileNumbers): string | null => personalMobileNumber ?? workMobileNumber ?? null;

interface HandoffOwnership {
  readonly status: HandoffStatus;
  readonly userUid: string | null;
  readonly currentUserUid: string | null | undefined;
}

export const isHandoffAssignedToCurrentUser = ({
  status,
  userUid,
  currentUserUid,
}: HandoffOwnership): boolean =>
  status === 'CLAIMED' && userUid != null && userUid === currentUserUid;

export const isHandoffClaimable = ({ status }: Pick<HandoffOwnership, 'status'>): boolean =>
  status === 'OPEN';

export const getHandoffAssignmentKey = ({
  status,
  userUid,
  currentUserUid,
}: HandoffOwnership): string => {
  if (isHandoffAssignedToCurrentUser({ status, userUid, currentUserUid })) {
    return 'aiTasks.detail.subtitle.assignedToMe';
  }

  if (isHandoffClaimable({ status })) {
    return 'aiTasks.detail.subtitle.awaitingClaim';
  }

  return 'aiTasks.detail.subtitle.claimedByOther';
};

interface HandoffBadgeInput extends HandoffOwnership {
  readonly handoffType: HandoffType;
  readonly severity: HandoffSeverity;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
}

export const getHandoffBadges = ({
  status,
  userUid,
  currentUserUid,
  handoffType,
  severity,
  t,
}: HandoffBadgeInput): readonly BadgeItem[] => {
  const badges: BadgeItem[] = [];

  if (isHandoffAssignedToCurrentUser({ status, userUid, currentUserUid })) {
    badges.push({ variant: 'success', label: t('aiTasks.badge.assignedToMe') });
  }

  if (isHandoffClaimable({ status })) {
    badges.push({ variant: 'neutral', label: t('aiTasks.badge.awaitingClaim') });
  }

  const severityVariant = SEVERITY_BADGE_VARIANTS[severity];
  const severityKey = HANDOFF_SEVERITY_TRANSLATION_KEYS[severity];

  if (severityVariant !== null && severityKey !== null) {
    badges.push({ variant: severityVariant, label: t(severityKey) });
  }

  badges.push({ variant: 'info', label: t(HANDOFF_TYPE_TRANSLATION_KEYS[handoffType]) });

  return badges;
};

export const HANDOFF_SEVERITY_TRANSLATION_KEYS: Record<HandoffSeverity, string | null> = {
  URGENT: 'aiTasks.severity.urgent',
  IMPORTANT: 'aiTasks.severity.important',
  MODERATELY_IMPORTANT: 'aiTasks.severity.moderatelyImportant',
  UNSPECIFIED: null,
};

const SEVERITY_BADGE_VARIANTS: Record<HandoffSeverity, BadgeItem['variant'] | null> = {
  URGENT: 'error',
  IMPORTANT: 'error',
  MODERATELY_IMPORTANT: 'warning',
  UNSPECIFIED: null,
};

export type HandoffSectionKey = 'important' | 'moderatelyImportant' | 'other';

// Ordered highest severity first, matching the SEVERITY_DESC ordering the list is fetched with.
// URGENT shares the important section: urgency shows as its own badge, and the standalone
// carousel above the list is fed by its own query for handoffs unassigned over 24h.
const SECTIONS: readonly {
  key: HandoffSectionKey;
  titleKey: string;
  severities: readonly HandoffSeverity[];
}[] = [
  { key: 'important', titleKey: 'aiTasks.section.important', severities: ['IMPORTANT', 'URGENT'] },
  {
    key: 'moderatelyImportant',
    titleKey: 'aiTasks.section.moderatelyImportant',
    severities: ['MODERATELY_IMPORTANT'],
  },
  { key: 'other', titleKey: 'aiTasks.section.other', severities: ['UNSPECIFIED'] },
];

export interface HandoffSection {
  readonly key: HandoffSectionKey;
  readonly titleKey: string;
  readonly data: readonly HandoffItem[];
}

export const getHandoffSections = (handoffs: readonly HandoffItem[]): readonly HandoffSection[] =>
  SECTIONS.map(({ key, titleKey, severities }) => ({
    key,
    titleKey,
    data: handoffs.filter((handoff) => severities.includes(handoff.severity)),
  })).filter((section) => section.data.length > 0);

export const getHandoffAction = ({
  status,
}: Pick<HandoffOwnership, 'status'>): HandoffAction | null => {
  if (isHandoffClaimable({ status })) {
    return 'claim';
  }

  // Anyone may hand a baton back, not just the person holding it.
  if (status === 'CLAIMED') {
    return 'release';
  }

  return null;
};
